/* /library-bundle/success?session_id=... — Post-Stripe landing.
 *
 * The flow (both authed + anonymous checkouts land here):
 *   1. Read `session_id` from the URL (Stripe substitutes it).
 *   2. POST /library-bundle/redeem — server verifies with Stripe, ensures a
 *      user exists for the buyer's email, grants the bundle, and returns
 *      an access_token + user. We stash the token in localStorage +
 *      call auth-context `refresh()` so the shell re-renders logged-in.
 *   3. Show a celebration card with two CTAs:
 *        • "Open your library →" (primary — the reason they paid)
 *        • "Start free 14-day trial" (secondary — the acquisition-loop
 *          nudge; only shown when `trial_available` comes back true).
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, BookOpen, Rocket, Loader2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function LibraryBundleSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();

  const [state, setState] = useState({
    loading: true,
    error: "",
    user: null,
    trialAvailable: false,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!sessionId) {
        setState((s) => ({
          ...s, loading: false,
          error: "Missing checkout session. If you just paid, check your email for the receipt.",
        }));
        return;
      }
      try {
        const { data } = await api.post("/library-bundle/redeem", { session_id: sessionId });
        if (cancelled) return;
        if (data?.access_token) {
          localStorage.setItem("af_token", data.access_token);
          // Rehydrate the auth context so the app shell reflects the
          // fresh identity if the buyer navigates into /app right after.
          try { await refresh(); } catch { /* non-fatal */ }
        }
        setState({
          loading: false, error: "",
          user: data?.user || null,
          trialAvailable: !!data?.trial_available,
        });
      } catch (err) {
        if (cancelled) return;
        setState((s) => ({
          ...s, loading: false,
          error:
            err?.response?.data?.detail ||
            "We couldn't verify your purchase automatically. Please check your email — you'll receive a receipt with a login link within a minute.",
        }));
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div
      className="min-h-screen bg-[#0B1D3B] text-white flex items-center justify-center px-4 py-10"
      data-testid="library-bundle-success-page"
    >
      <div className="max-w-2xl w-full">
        {state.loading ? (
          <LoadingCard />
        ) : state.error ? (
          <ErrorCard message={state.error} />
        ) : (
          <SuccessCard user={state.user} trialAvailable={state.trialAvailable} />
        )}
      </div>
    </div>
  );
}

function LoadingCard() {
  return (
    <div
      className="bg-white/5 border border-white/10 rounded-2xl p-10 text-center backdrop-blur"
      data-testid="library-success-loading"
    >
      <Loader2 size={32} className="animate-spin mx-auto text-[#F59E0B] mb-4" />
      <div className="text-xl font-display font-bold">Unlocking your library…</div>
      <div className="text-white/60 text-sm mt-2">
        Confirming the payment with Stripe. This takes a couple of seconds.
      </div>
    </div>
  );
}

function ErrorCard({ message }) {
  return (
    <div
      className="bg-white/5 border border-[#F59E0B]/30 rounded-2xl p-10 text-center backdrop-blur"
      data-testid="library-success-error"
    >
      <AlertTriangle size={32} className="mx-auto text-[#F59E0B] mb-4" />
      <div className="text-xl font-display font-bold">Something wasn&apos;t quite right</div>
      <p className="text-white/70 text-sm mt-3 leading-relaxed">{message}</p>
      <Link
        to="/login?next=/app/paid-library"
        className="inline-flex items-center gap-2 mt-6 bg-[#F59E0B] text-[#0B1D3B] font-bold px-5 py-2.5 rounded-lg"
        data-testid="library-success-login-link"
      >
        Log in to my library →
      </Link>
    </div>
  );
}

function SuccessCard({ user, trialAvailable }) {
  const firstName = (user?.name || user?.email || "operator").split(" ")[0].split("@")[0];
  return (
    <div
      className="bg-white/5 border border-emerald-400/30 rounded-2xl p-8 md:p-10 backdrop-blur"
      data-testid="library-success-card"
    >
      <div className="flex items-center gap-3 text-emerald-300 mb-3">
        <CheckCircle2 size={22} />
        <span className="text-[11px] tracking-[0.2em] uppercase font-bold">Payment received</span>
      </div>

      <h1 className="font-display text-3xl md:text-4xl font-black leading-tight mb-3">
        You&apos;re in, {firstName}. <span className="text-[#F59E0B]">All 15 eBooks are unlocked.</span>
      </h1>
      <p className="text-white/70 text-base leading-relaxed max-w-xl">
        Lifetime access — download anytime, all future updates included.
        We just emailed you a receipt with a direct login link too.
      </p>

      <div className="mt-8 flex flex-col sm:flex-row gap-3">
        <Link
          to="/v3/learn/paid-library"
          className="inline-flex items-center justify-center gap-2 bg-[#F59E0B] text-[#0B1D3B] font-extrabold px-6 py-3.5 rounded-lg hover:brightness-110 transition"
          data-testid="library-success-open-library"
        >
          <BookOpen size={16} /> Open your library
        </Link>
        {trialAvailable && (
          <Link
            to="/register?utm_source=library_bundle&utm_medium=success_page&utm_campaign=post_purchase"
            className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white/25 text-white font-bold px-6 py-3.5 rounded-lg hover:bg-white/10 transition"
            data-testid="library-success-start-trial"
          >
            <Rocket size={16} /> Start free 14-day trial
          </Link>
        )}
      </div>

      {trialAvailable && (
        <div
          className="mt-8 border-t border-white/10 pt-6 text-white/70 text-sm leading-relaxed"
          data-testid="library-success-trial-pitch"
        >
          <div className="font-bold text-white mb-1">
            While those eBooks marinate…
          </div>
          Every playbook in your library is baked into the app —
          Deal Finder, Skip Trace, Apex 2.0 AI Receptionist, Dispo Studio,
          buyer Deal Rooms. Try it free for 14 days. <span className="text-white/50">
            No credit card required.
          </span>
        </div>
      )}
    </div>
  );
}
